import React, { useEffect, useMemo, useState } from 'react';
import ModalPortal from './ModalPortal';

const money = (value) => new Intl.NumberFormat('es-MX', {
  style: 'currency', currency: 'MXN', maximumFractionDigits: 0,
}).format(Number(value || 0));

const motivoTexto = (motivo) => ({
  pausa_global: 'Los envíos están pausados.',
  sin_telefono: 'Falta capturar el teléfono.',
  sin_atraso: 'El contrato ya no tiene cuotas vencidas.',
  cuota_pagada: 'La cuota ya fue pagada.',
  caducado: 'Caducó: ya pasó la ventana permitida para este aviso.',
  superado_por_mas_reciente: 'Se descartó porque hay un aviso más reciente para la misma cuota.',
  cubierto_por_preventivo: 'No hace falta: el recordatorio de hoy ya menciona lo que debe y el total.',
  opt_out: 'El cliente pidió no recibir mensajes.',
  modo_humano: 'Un cobrador está atendiendo esta conversación.',
  promesa_pendiente: 'Hay una promesa o reporte de pago pendiente.',
  espaciado_mismo_telefono: 'Debe esperar 30 minutos antes del siguiente mensaje a este teléfono.',
  tope_diario_telefono: 'Ese teléfono alcanzó el tope diario.',
  preview_no_disponible: 'No se pudo construir la vista previa.',
}[motivo] || (motivo ? motivo.replaceAll('_', ' ') : 'Listo para enviar'));

/*
 * Espaciado mínimo entre dos mensajes al MISMO teléfono (config del backend:
 * cobranza.limites.espaciado_minutos). Aquí solo sirve para proyectar la hora
 * estimada en el resumen; quien decide de verdad es el motor.
 */
const ESPACIADO_MIN = 30;

/* "18 min", "1 h 5 min" — el cobrador necesita saber cuánto falta, no un ISO. */
const minutosTexto = (minutos) => {
  const total = Math.max(1, Math.round(Number(minutos) || 0));
  if (total < 60) return `${total} min`;
  const horas = Math.floor(total / 60);
  const resto = total % 60;
  return resto ? `${horas} h ${resto} min` : `${horas} h`;
};

/*
 * Minutos que le faltan a una fila para poder salir, o null si sale ya. El
 * backend lo manda calculado contra el último mensaje real a ese teléfono: no
 * se recalcula aquí para no inventar una segunda verdad.
 */
const esperaMinutos = (fila) => {
  const valor = Number(fila?.sale_en_minutos);
  return Number.isFinite(valor) && valor > 0 ? Math.round(valor) : null;
};

/*
 * "Autorizado" solo explica la espera de los 30 minutos. Cualquier otro corte
 * (tope diario, opt-out, sin teléfono…) es un problema de verdad y tiene que
 * seguir a la vista aunque un humano ya haya dado el OK: la autorización no es
 * un salvoconducto.
 */
const esperaExplicada = (fila) => Boolean(fila?.autorizado)
  && (!fila.motivo || fila.motivo === 'espaciado_mismo_telefono');

/*
 * `.av-confirm-list span` fuerza columna a TODO span de la lista, y una
 * etiqueta en columna se parte en dos renglones. Se devuelve en línea para que
 * gane sobre esa regla sin tener que tocar la hoja de estilos compartida.
 */
const CHIP_INLINE = {
  alignSelf: 'flex-start', display: 'inline-flex', flex: 'none',
  flexDirection: 'row', alignItems: 'center', gap: 5,
};

/* Estados que devuelve Meta, en español. */
const ESTADO_ENVIO = {
  sent: 'Enviado', delivered: 'Entregado', read: 'Leído', failed: 'Falló', pending: 'En cola',
};

const ICONO_ORIGEN = {
  automatico: 'pi-bolt', semiautomatico: 'pi-check-square', interno: 'pi-bell', manual: 'pi-user',
};

const SECCIONES = [
  ['vencido', 'Ya se les pasó', 'pi-exclamation-circle'],
  ['hoy', 'Vence hoy', 'pi-clock'],
  ['proximo', 'Por vencer', 'pi-calendar'],
];

const iniciales = (nombre) => (nombre || '?')
  .split(/\s+/).filter(Boolean).slice(0, 2)
  .map((parte) => parte[0])
  .join('')
  .toUpperCase();

const fechaLarga = (iso) => {
  if (!iso) return '';
  const fecha = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(fecha.getTime())) return '';
  return fecha.toLocaleDateString('es-MX', { day: 'numeric', month: 'long' });
};

/*
 * Por qué una persona puede quedar sin botón de aprobar. Se listan los motivos
 * distintos (un cliente con 2 autos puede traer dos causas diferentes) porque
 * un botón deshabilitado sin explicación deja al cobrador sin saber qué hacer.
 */
const motivosBloqueo = (avisos) => {
  const unicos = [];
  (avisos || []).forEach((aviso) => {
    if (aviso.puede_enviar) return;
    // Un aviso ya autorizado no es un bloqueo: está en camino y trae su propia
    // etiqueta. Listarlo aquí haría leer "hay que esperar 30 minutos" como si
    // algo estuviera mal, cuando es justo lo que se pidió que pasara.
    if (esperaExplicada(aviso)) return;
    let texto = 'No está listo para enviarse.';
    if (aviso.preview_pendiente) texto = 'Preparando la vista previa…';
    else if (aviso.motivo) texto = motivoTexto(aviso.motivo);
    if (!unicos.includes(texto)) unicos.push(texto);
  });
  return unicos.slice(0, 2).join(' · ');
};

export const AvisosColaModal = ({ data, busy, notice, api, onClose, onAction, onMode, onTab }) => {
  const [confirmacion, setConfirmacion] = useState(null);
  const [seleccionados, setSeleccionados] = useState([]);
  // Se apaga en cada apertura del resumen a propósito: es una excepción que se
  // toma mensaje por mensaje, no una preferencia que se queda encendida.
  const [forzarEspaciado, setForzarEspaciado] = useState(false);
  const [gruposAbiertos, setGruposAbiertos] = useState([]);
  const [personasAbiertas, setPersonasAbiertas] = useState([]);
  const [tab, setTab] = useState(null);
  const [vista, setVista] = useState('hoy');
  // Respuesta de `vista=todo`. El padre solo carga la bandeja del día, así que
  // el calendario completo lo pide este modal y vive aquí.
  const [todo, setTodo] = useState(null);
  const [todoBusy, setTodoBusy] = useState(false);
  // Vistas previas pedidas una por una, por id de recordatorio. El listado solo
  // trae las primeras (tope del backend); estas se piden al desplegar la
  // tarjeta. Sin esto la fila se quedaba en "Cargando…" para siempre.
  const [previews, setPreviews] = useState({});
  const [todoError, setTodoError] = useState(null);
  const [reintento, setReintento] = useState(0);

  /*
   * El backend anterior no manda `vista`. Sin esa llave no hay forma de saber
   * si `grupos` ya viene filtrado al día de hoy, y esconder el calendario a
   * ciegas dejaría la cola entera invisible: en ese caso el modal se queda con
   * el comportamiento de siempre y ni siquiera ofrece el selector.
   */
  const soportaVistas = Boolean(data?.vista);
  const enTodo = !soportaVistas || vista === 'todo';

  useEffect(() => {
    if (!soportaVistas || vista !== 'todo') return undefined;
    const ctrl = new AbortController();
    setTodoBusy(true);
    setTodoError(null);
    api.getAvisos(ctrl.signal, tab, 'todo')
      .then((res) => { if (!ctrl.signal.aborted) setTodo(res?.data || null); })
      .catch((error) => {
        if (ctrl.signal.aborted || error?.code === 'ERR_CANCELED') return;
        setTodoError('No se pudo cargar el calendario completo.');
      })
      .finally(() => { if (!ctrl.signal.aborted) setTodoBusy(false); });
    return () => ctrl.abort();
    // `data` en las dependencias: cuando el padre recarga tras un envío, el
    // calendario que se está viendo también tiene que refrescarse.
  }, [api, soportaVistas, vista, tab, data, reintento]);

  const fuente = soportaVistas && vista === 'todo' ? todo : data;
  const grupos = useMemo(() => fuente?.grupos || [], [fuente]);
  const personas = useMemo(() => data?.personas || [], [data]);
  // El historial ya viajaba en la respuesta pero no se pintaba en el modal:
  // un aviso enviado desaparecía de la bandeja y no había dónde verlo.
  const historial = useMemo(() => data?.historial || [], [data]);
  const enHistorial = soportaVistas && vista === 'historial';
  const perdidos = data?.perdidos || fuente?.perdidos || null;
  // Modo y pausa se leen SIEMPRE del padre: es la copia que se recarga al
  // cambiarlos, la del calendario local puede haber quedado atrás.
  const semiautomatico = data?.modo === 'borrador';
  const automatico = data?.modo === 'automatico';
  const porEstado = useMemo(() => Object.fromEntries(SECCIONES.map(([estado]) => [
    estado, grupos.filter((grupo) => grupo.estado === estado),
  ])), [grupos]);

  const conteo = (estado) => (porEstado[estado] || []).reduce((total, grupo) => total + grupo.filas.length, 0);

  const avisosHoy = useMemo(() => personas.flatMap((persona) => persona.avisos || []), [personas]);
  const filasVisibles = enTodo ? grupos.flatMap((grupo) => grupo.filas) : avisosHoy;

  // El backend solo previsualiza las primeras filas (cada vista previa cuesta
  // varias consultas). Se avisa para que no parezca un error.
  const sinPrevisualizar = filasVisibles.filter((fila) => fila.preview_pendiente).length;

  /*
   * Tab inicial: la primera que tenga avisos, priorizando lo urgente
   * (vencido → hoy → próximo). Abrir en una pestaña vacía haría creer que no
   * hay nada que revisar. `tab` arranca en null para que este cálculo mande
   * hasta que el usuario elija a mano.
   */
  const tabActiva = tab ?? (SECCIONES.find(([estado]) => conteo(estado) > 0)?.[0] ?? 'vencido');
  const gruposTab = porEstado[tabActiva] || [];

  const abrirConfirmacion = (grupo) => {
    // Las ya autorizadas NO entran: un humano les dio el OK y el motor las
    // tiene en cola. Volver a mandarlas sería aprobar dos veces el mismo
    // mensaje, que es justo lo que este cambio vino a quitar.
    const ids = grupo.filas
      .filter((fila) => fila.puede_enviar && !fila.autorizado)
      .map((fila) => fila.recordatorio_id);
    setSeleccionados(ids);
    setForzarEspaciado(false);
    setConfirmacion(grupo);
  };

  const cerrarConfirmacion = () => {
    setConfirmacion(null);
    setForzarEspaciado(false);
  };

  /*
   * 🔴 EL RESUMEN. Nace de un caso real: la tarjeta decía "sale 2 mensajes", el
   * cobrador aprobó, salió UNO y el otro quedó esperando el espaciado —correcto,
   * pero invisible—. Aquí se escribe qué sale, con qué texto, cuándo, y qué no
   * sale y por qué. Nada se calla.
   *
   * Lo que este cálculo NO hace: recalcular la deduplicación. Cuántos WhatsApps
   * salen de verdad lo dice `persona.mensajes`, que el backend arma con la MISMA
   * clave que usa el enviador; copiar esa regla aquí sería tener dos verdades y
   * que una se quede atrás.
   */
  const resumen = useMemo(() => {
    if (!confirmacion) return null;
    const filas = confirmacion.filas || [];
    /*
     * La tarjeta de PERSONA agrupa por teléfono normalizado: todo lo suyo va al
     * mismo número y el espaciado enfrenta a estos mensajes entre sí. El grupo
     * por fecha mezcla clientes distintos —teléfonos distintos— y ahí el
     * espaciado no aplica entre ellos: afirmarlo sería mentir al revés.
     */
    const mismoTelefono = Boolean(confirmacion.persona_key);
    const aprobables = filas.filter((fila) => fila.puede_enviar && !fila.autorizado);
    const elegidas = aprobables.filter((fila) => seleccionados.includes(fila.recordatorio_id));
    const quitadas = aprobables.filter((fila) => !seleccionados.includes(fila.recordatorio_id));
    const yaAutorizadas = filas.filter((fila) => fila.autorizado);
    const bloqueadas = filas.filter((fila) => !fila.puede_enviar && !fila.autorizado);

    // Tope del backend acotado a lo que quedó seleccionado: quitar filas solo
    // puede bajar el número de mensajes, nunca subirlo.
    const mensajesPersona = Number(confirmacion.mensajes ?? 0);
    const mensajes = mismoTelefono && mensajesPersona > 0
      ? Math.min(mensajesPersona, elegidas.length)
      : elegidas.length;
    // Varios avisos viajando en un solo WhatsApp (la mora del mismo teléfono se
    // resume). Con esto los tiempos de abajo son estimados, no promesas.
    const seJuntan = elegidas.length - mensajes;

    // Si el teléfono recibió algo hace poco, ni el primero sale al momento.
    const esperaInicial = elegidas.reduce(
      (mayor, fila) => Math.max(mayor, esperaMinutos(fila) ?? 0),
      0,
    );

    let ahora;
    if (mismoTelefono) {
      ahora = esperaInicial > 0 ? 0 : Math.min(1, mensajes);
    } else {
      ahora = elegidas.filter((fila) => esperaMinutos(fila) === null).length;
    }
    const diferidos = Math.max(0, mensajes - ahora);
    // Solo se puede prometer el turno fila por fila cuando cada aviso es su
    // propio mensaje; con resumen de mora no hay forma de saber cuál cae en
    // cuál sin copiar la deduplicación del backend.
    const turnosFiables = mismoTelefono && seJuntan === 0;

    /*
     * Turno estimado de cada fila. Tres estados a propósito:
     *   ahora    → sale en cuanto se apriete;
     *   espera   → queda autorizado y se sabe cuánto le falta;
     *   incierto → queda autorizado, pero el orden lo decide el motor (varios
     *              avisos de mora viajan en un solo mensaje). Se dice
     *              "incierto" en vez de inventar un minuto: prometer una hora
     *              que no se cumple es la misma mentira que se vino a quitar,
     *              nada más que al revés.
     */
    const turnos = {};
    elegidas.forEach((fila, indice) => {
      const espera = esperaMinutos(fila);
      if (!mismoTelefono) {
        // Teléfonos distintos: no compiten entre sí, cada fila espera lo suyo.
        turnos[fila.recordatorio_id] = espera === null
          ? { estado: 'ahora', minutos: 0 }
          : { estado: 'espera', minutos: espera };
      } else if (diferidos === 0) {
        turnos[fila.recordatorio_id] = { estado: 'ahora', minutos: 0 };
      } else if (!turnosFiables) {
        turnos[fila.recordatorio_id] = { estado: 'incierto', minutos: 0 };
      } else {
        // Mismo teléfono y un mensaje por aviso: cada uno sale un espaciado
        // después del anterior, arrancando desde lo que ya venía esperando.
        const minutos = esperaInicial + (indice * ESPACIADO_MIN);
        turnos[fila.recordatorio_id] = minutos === 0
          ? { estado: 'ahora', minutos: 0 }
          : { estado: 'espera', minutos };
      }
    });

    return {
      filas,
      mismoTelefono,
      elegidas,
      quitadas,
      yaAutorizadas,
      bloqueadas,
      mensajes,
      seJuntan,
      esperaInicial,
      ahora,
      diferidos,
      turnos,
    };
  }, [confirmacion, seleccionados]);

  /*
   * Con el switch encendido ya no hay espera que anunciar. La pantalla tiene
   * que decir lo que va a pasar DE VERDAD si se aprieta ahora, no lo que
   * pasaría sin él: un resumen que no reacciona al switch vuelve a ser el
   * mismo cartel que miente.
   */
  const saltaEspera = Boolean(forzarEspaciado) && Boolean(resumen?.diferidos);

  const confirmar = () => {
    if (!seleccionados.length) return;
    const forzar = forzarEspaciado;
    onAction(
      'grupo-confirmado',
      () => api.enviarAvisosGrupo(seleccionados, forzar),
      'No se pudo procesar el grupo.',
    );
    cerrarConfirmacion();
  };

  const alternarGrupo = (grupoId) => {
    setGruposAbiertos((actuales) => (actuales.includes(grupoId)
      ? actuales.filter((id) => id !== grupoId)
      : [...actuales, grupoId]));
  };

  /*
   * Al desplegar una tarjeta se piden las vistas previas que faltan. El listado
   * solo trae las primeras (el backend tiene un tope: cada preview cuesta ~3
   * consultas), y antes el resto se quedaba en "Cargando…" para siempre porque
   * nadie las pedía nunca.
   *
   * Silencioso a propósito: si una falla, la fila se queda como estaba y el
   * cobrador puede seguir con las demás. No vale abortar la pantalla por una.
   */
  const cargarPreviewsFaltantes = (avisos) => {
    (avisos || [])
      .filter((fila) => fila.preview_pendiente && !previews[fila.recordatorio_id])
      .forEach((fila) => {
        api.previewAviso(fila.recordatorio_id)
          .then((res) => {
            const nueva = res?.data?.fila;
            if (nueva) setPreviews((actuales) => ({ ...actuales, [fila.recordatorio_id]: nueva }));
          })
          .catch(() => {});
      });
  };

  const alternarPersona = (clave, avisos) => {
    const abriendo = !personasAbiertas.includes(clave);
    if (abriendo) cargarPreviewsFaltantes(avisos);
    setPersonasAbiertas((actuales) => (actuales.includes(clave)
      ? actuales.filter((id) => id !== clave)
      : [...actuales, clave]));
  };

  const cambiarTab = (estado) => {
    if (tabActiva === estado) return;
    setTab(estado);
    // Con el contrato nuevo el propio modal vuelve a pedir el calendario (el
    // efecto depende de `tab`); en modo compatibilidad recarga el padre.
    if (!soportaVistas && onTab) onTab(estado);
  };

  /*
   * Etiqueta de una fila ya autorizada. Va SIEMPRE visible, nunca en un
   * tooltip: el cobrador tiene que distinguir de un vistazo lo que falta
   * aprobar, lo que ya aprobó y está en camino, y lo que no va a salir. Un
   * aviso que espera su turno sin decirlo se lee como un aviso perdido.
   */
  const chipAutorizado = (fila, extra = {}) => {
    const espera = esperaMinutos(fila);
    return (
      <span
        className="av-status cyan"
        style={{ ...CHIP_INLINE, marginTop: 6, ...extra }}
        title={fila.autorizado_at ? `Autorizado el ${fila.autorizado_at}` : undefined}
      >
        <i className="pi pi-check-circle" />
        Autorizado · {espera !== null
          ? `sale en ${minutosTexto(espera)}`
          : 'sale en el próximo ciclo'}
      </span>
    );
  };

  // Fila de aviso: misma pieza en el calendario y dentro de la tarjeta de una
  // persona, para que un aviso se vea y se opere igual en los dos lados.
  const filaAviso = (fila) => (
    <div className="av-notice-row" key={fila.recordatorio_id}>
      <div className="av-avatar">{iniciales(fila.cliente)}</div>
      <div className="av-notice-main">
        <div><b>{fila.cliente}</b><span>{fila.clave_contrato} · {fila.auto}</span></div>
        <p>{fila.preview_pendiente
          ? 'Cargando la vista previa…'
          : (fila.vista_previa || motivoTexto(fila.motivo))}</p>
        {Boolean(fila.autorizado) && chipAutorizado(fila)}
        {/* El motivo se calla SOLO cuando la etiqueta de arriba ya lo explica
            (la espera de los 30 minutos). Cualquier otro corte se sigue
            escribiendo aunque la fila esté autorizada. */}
        {!fila.puede_enviar && !fila.preview_pendiente && !esperaExplicada(fila) && <small className="av-block-reason"><i className="pi pi-info-circle" />{motivoTexto(fila.motivo)}</small>}
      </div>
      <strong className="av-row-money">{money(fila.monto)}</strong>
      <div className="av-row-actions">
        {semiautomatico && <button type="button" className="send" disabled={Boolean(busy) || data?.pausado || !fila.puede_enviar} title={fila.puede_enviar ? '' : motivoTexto(fila.motivo)} onClick={() => onAction(`send-${fila.recordatorio_id}`, () => api.enviarAviso(fila.recordatorio_id), 'No se pudo enviar el aviso.')}><i className="pi pi-send" />Enviar</button>}
        {(semiautomatico || automatico) && fila.decision !== 'cancelar' && <button type="button" className="exclude" disabled={Boolean(busy)} onClick={() => onAction(`exclude-${fila.recordatorio_id}`, () => api.excluirAviso(fila.recordatorio_id), 'No se pudo excluir el aviso.')}><i className="pi pi-ban" />Excluir</button>}
      </div>
    </div>
  );

  return (
    <ModalPortal>
      <div className="av-modal-backdrop" role="presentation" onMouseDown={onClose}>
        <div className="av-modal" role="dialog" aria-modal="true" aria-label="Cola de avisos de pago" onMouseDown={(event) => event.stopPropagation()}>
          <div className="av-modal-head">
            <div>
              <h3>Cola de avisos de pago</h3>
              <p>{vista === 'historial'
                ? 'Lo que ya salió: qué mensaje, a qué contrato y quién lo autorizó.'
                : (enTodo
                  ? 'Calendario completo: vencidos, hoy y próximos.'
                  : 'Lo que se puede enviar hoy, agrupado por persona.')}</p>
            </div>
            <button type="button" aria-label="Cerrar" onClick={onClose}><i className="pi pi-times" /></button>
          </div>
          {soportaVistas && (
            <div className="av-modal-toolbar">
              <span>Vista</span>
              <button type="button" className={vista === 'hoy' ? 'active' : ''} onClick={() => setVista('hoy')}>
                <i className="pi pi-inbox" /> Para enviar hoy{personas.length > 0 ? ` (${personas.length})` : ''}
              </button>
              <button type="button" className={vista === 'todo' ? 'active' : ''} onClick={() => setVista('todo')}>
                <i className="pi pi-calendar" /> Todo el calendario
              </button>
              {/* El aviso enviado desaparece de la bandeja (ya no es trabajo) y
                  antes no había dónde verlo sin salirse del modal. */}
              <button type="button" className={vista === 'historial' ? 'active' : ''} onClick={() => setVista('historial')}>
                <i className="pi pi-history" /> Historial{historial.length > 0 ? ` (${historial.length})` : ''}
              </button>
            </div>
          )}
          <div className="av-modal-toolbar">
            <span>Modo de salida</span>
            <button type="button" className={semiautomatico ? 'active' : ''} disabled={Boolean(busy)} onClick={() => onMode('borrador')}><i className="pi pi-check-square" /> Semiautomático</button>
            <button type="button" className={automatico ? 'active' : ''} disabled={Boolean(busy)} onClick={() => onMode('automatico')}><i className="pi pi-bolt" /> Automático</button>
          </div>
          {data?.modo === 'manual' && <div className="av-inline-alert warn"><i className="pi pi-info-circle" />El modo anterior sigue en Manual. Elige Semiautomático o Automático para preparar la cola.</div>}
          {data?.pausado && <div className="av-inline-alert warn"><i className="pi pi-pause-circle" />Los envíos están pausados.</div>}
          {notice && <div className={`av-inline-alert ${notice.type}`}><i className="pi pi-info-circle" />{notice.text}</div>}
          {/* 🔴 Avisos que murieron sin salir. La bandeja del día esconde lo que
              no es de hoy, así que este contador es la ÚNICA señal de que el
              proceso está fallando: en julio caducaron 44 sin que nadie lo
              notara. Va siempre visible, nunca en un tooltip. */}
          {Number(perdidos?.total) > 0 && (
            <div className="av-inline-alert error">
              <i className="pi pi-exclamation-triangle" />
              <span>
                <b>{perdidos.total} avisos no salieron</b> en los últimos {perdidos.dias} días
                {fechaLarga(perdidos.desde) ? ` (desde el ${fechaLarga(perdidos.desde)})` : ''}: caducaron sin
                aprobarse. Los preventivos no se vuelven a generar.
              </span>
            </div>
          )}
          {sinPrevisualizar > 0 && !enHistorial && (
            <div className="av-inline-alert warn">
              <i className="pi pi-info-circle" />
              {enTodo
                ? `${sinPrevisualizar} avisos más en la cola sin vista previa todavía. Se muestran primero los más urgentes; aprueba esos y el resto se irá cargando.`
                : `${sinPrevisualizar} avisos de hoy siguen sin vista previa. Se calculan primero los más urgentes; en cuanto carguen se podrán aprobar.`}
            </div>
          )}
          {/* Tabs en vez de secciones apiladas: con 3 bloques largos había que
              hacer mucho scroll para llegar a "Por vencer". */}
          {enTodo && grupos.length > 0 && (
            <div className="av-tabs" role="tablist">
              {SECCIONES.map(([estado, titulo, icono]) => (
                <button
                  type="button"
                  key={estado}
                  role="tab"
                  aria-selected={tabActiva === estado}
                  className={`av-tab ${estado} ${tabActiva === estado ? 'active' : ''}`}
                  onClick={() => cambiarTab(estado)}
                >
                  <i className={`pi ${icono}`} />
                  <span>{titulo}</span>
                  <b>{conteo(estado)}</b>
                </button>
              ))}
            </div>
          )}
          <div className="av-modal-body">
            {enHistorial && (
              historial.length === 0 ? (
                <div className="av-modal-empty">
                  <i className="pi pi-inbox" />
                  <b>Todavía no se ha enviado ningún aviso</b>
                  <span>Aquí van a aparecer los mensajes que salgan, con quién los autorizó.</span>
                </div>
              ) : (
                <div className="av-table-scroll">
                  <table className="av-table">
                    <thead><tr>
                      <th>Fecha</th><th>Cliente</th><th>Plantilla</th><th>Contrato</th><th>Estado</th><th>Origen</th>
                    </tr></thead>
                    <tbody>
                      {historial.map((row) => (
                        <tr key={row.id}>
                          <td className="tnum">{row.fecha}</td>
                          <td><b>{row.cliente}</b></td>
                          <td>{row.plantilla}</td>
                          <td className="tnum">{row.contrato}</td>
                          <td>{ESTADO_ENVIO[row.estado] || row.estado}</td>
                          <td>
                            <div className={`av-origin ${row.origen?.tipo}`}>
                              <i className={`pi ${ICONO_ORIGEN[row.origen?.tipo] || 'pi-user'}`} />
                              <span><b>{row.origen?.label}</b><small>{row.origen?.detalle}</small></span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
            {!enTodo && !enHistorial && (
              personas.length === 0 ? (
                <div className="av-modal-empty">
                  <i className="pi pi-check-circle" />
                  <b>No hay avisos por enviar hoy</b>
                  <span>Cuando la cadencia programe el siguiente recordatorio aparecerá aquí.</span>
                  <button type="button" className="av-primary" style={{ marginTop: 14 }} onClick={() => setVista('todo')}>
                    <i className="pi pi-calendar" /> Ver todo el calendario
                  </button>
                </div>
              ) : (
                /* Una tarjeta por PERSONA: un cliente con dos autos traía hasta
                   cinco renglones sueltos y 23 clientes parecían 61. */
                <section className="av-section">
                  {personas.map((persona) => {
                    const avisos = (persona.avisos || []).map((fila) => previews[fila.recordatorio_id] || fila);
                    // Ya autorizadas: aprobadas, esperando su turno. No vuelven a
                    // pedir permiso, así que no cuentan para el botón — pero SÍ se
                    // enseñan, o desaparecen sin rastro para quien las aprobó.
                    const autorizadas = avisos.filter((fila) => fila.autorizado);
                    const porAprobar = avisos.filter((fila) => fila.puede_enviar && !fila.autorizado);
                    const contratos = new Set(avisos.map((fila) => fila.clave_contrato)).size;
                    // Cuántos WhatsApps salen de verdad: el backend lo calcula con la
                    // misma deduplicación que usa el enviador. Puede ser menor que
                    // `porAprobar` porque la mora de varios autos se junta en uno.
                    const mensajes = Number(persona.mensajes ?? 0);
                    const abierta = personasAbiertas.includes(persona.persona_key);
                    // Se decide con las filas de esta pantalla (que pueden traer una
                    // vista previa cargada después) y no con `persona.puede_enviar`,
                    // que se calculó antes y esconde botones que ya son válidos.
                    const puede = porAprobar.length > 0;
                    // Nunca un botón muerto: si no se puede aprobar, en su lugar
                    // va el motivo escrito (sin teléfono, opt-out, pausa…).
                    const bloqueo = data?.pausado
                      ? 'Los envíos están pausados.'
                      : (puede || autorizadas.length ? '' : (motivosBloqueo(avisos) || 'No hay avisos habilitados.'));
                    // La espera más corta de las autorizadas: es la próxima señal de
                    // vida que va a ver el cobrador.
                    const proximaEspera = autorizadas.reduce((menor, fila) => {
                      const espera = esperaMinutos(fila);
                      if (espera === null) return menor;
                      return menor === null ? espera : Math.min(menor, espera);
                    }, null);
                    return (
                      <article className={`av-group ${abierta ? 'open' : 'collapsed'}`} key={persona.persona_key}>
                        <header
                          role="button"
                          tabIndex={0}
                          aria-expanded={abierta}
                          onClick={() => alternarPersona(persona.persona_key, avisos)}
                          onKeyDown={(event) => {
                            if (event.target !== event.currentTarget) return;
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              alternarPersona(persona.persona_key, avisos);
                            }
                          }}
                        >
                          <span className="av-day">{iniciales(persona.cliente)}</span>
                          <div>
                            <b>{persona.cliente}</b>
                            <small>
                              {avisos.length} {avisos.length === 1 ? 'aviso' : 'avisos'}
                              {contratos > 1 ? ` · ${contratos} contratos` : ''}
                              {/* Los avisos de mora del mismo teléfono se juntan en UN
                                  solo WhatsApp al enviar. Sin esta aclaración la tarjeta
                                  decía "Aprobar 2" y salía uno: el contador mentía. */}
                              {mensajes > 0 && mensajes < porAprobar.length
                                ? ` · sale ${mensajes === 1 ? '1 mensaje' : `${mensajes} mensajes`}`
                                : ''}
                            </small>
                            {semiautomatico && autorizadas.length > 0 && (
                              <span className="av-status cyan" style={{ ...CHIP_INLINE, marginTop: 6 }}>
                                <i className="pi pi-check-circle" />
                                {autorizadas.length === 1 ? 'Autorizado' : `${autorizadas.length} autorizados`}
                                {proximaEspera !== null
                                  ? ` · sale en ${minutosTexto(proximaEspera)}`
                                  : ' · en camino'}
                              </span>
                            )}
                            {semiautomatico && bloqueo && (
                              <span className="av-block-reason"><i className="pi pi-info-circle" />{bloqueo}</span>
                            )}
                          </div>
                          <strong>{money(persona.total)}</strong>
                          {semiautomatico && puede && !data?.pausado && (
                            <button type="button" disabled={Boolean(busy)} onClick={(event) => { event.stopPropagation(); abrirConfirmacion({ ...persona, filas: avisos }); }}>
                              Aprobar {porAprobar.length}
                            </button>
                          )}
                          <i className={`av-group-chevron pi ${abierta ? 'pi-chevron-up' : 'pi-chevron-down'}`} aria-hidden="true" />
                        </header>
                        {abierta && <div className="av-group-rows">{avisos.map(filaAviso)}</div>}
                      </article>
                    );
                  })}
                </section>
              )
            )}
            {enTodo && todoBusy && !fuente && (
              <div className="av-state"><i className="pi pi-spin pi-spinner" /> Cargando el calendario…</div>
            )}
            {/* Si ya había datos en pantalla, un refresco fallido se avisa sin
                borrarlos; solo cuando no hay nada se toma la pantalla completa. */}
            {enTodo && todoError && Boolean(fuente) && (
              <div className="av-inline-alert error"><i className="pi pi-times-circle" />{todoError} Se muestra la última versión cargada.</div>
            )}
            {enTodo && todoError && !fuente && (
              <div className="av-state error">
                <b>{todoError}</b>
                <button type="button" onClick={() => setReintento((valor) => valor + 1)}>Reintentar</button>
              </div>
            )}
            {enTodo && Boolean(fuente || (!todoBusy && !todoError)) && (
              grupos.length === 0 ? (
                <div className="av-modal-empty">
                  <i className="pi pi-inbox" />
                  <b>Sin avisos por revisar</b>
                  <span>No hay recordatorios visibles en este momento.</span>
                  {soportaVistas && (
                    <button type="button" className="av-primary" style={{ marginTop: 14 }} onClick={() => setVista('hoy')}>
                      <i className="pi pi-inbox" /> Volver a la bandeja de hoy
                    </button>
                  )}
                </div>
              ) : (
                <section className="av-section" key={tabActiva}>
                  {gruposTab.length === 0 ? <div className="av-section-empty">Sin avisos en esta pestaña</div> : gruposTab.map((grupo) => {
                    const porAprobar = grupo.filas.filter((fila) => fila.puede_enviar && !fila.autorizado);
                    const autorizadas = grupo.filas.filter((fila) => fila.autorizado);
                    const grupoId = `${grupo.estado}-${grupo.fecha || grupo.dia}`;
                    const abierto = gruposAbiertos.includes(grupoId);
                    return (
                      <article className={`av-group ${grupo.estado} ${abierto ? 'open' : 'collapsed'}`} key={grupoId}>
                        <header
                          role="button"
                          tabIndex={0}
                          aria-expanded={abierto}
                          onClick={() => alternarGrupo(grupoId)}
                          onKeyDown={(event) => {
                            if (event.target !== event.currentTarget) return;
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              alternarGrupo(grupoId);
                            }
                          }}
                        >
                          <span className="av-day">{grupo.estado === 'hoy' ? 'HOY' : grupo.dia}</span>
                          <div>
                            <b>{grupo.titulo}</b>
                            <small>{grupo.filas.length} {grupo.filas.length === 1 ? 'aviso' : 'avisos'}</small>
                            {semiautomatico && autorizadas.length > 0 && (
                              <span className="av-status cyan" style={{ ...CHIP_INLINE, marginTop: 6 }}>
                                <i className="pi pi-check-circle" />
                                {autorizadas.length === 1 ? '1 autorizado, en camino' : `${autorizadas.length} autorizados, en camino`}
                              </span>
                            )}
                          </div>
                          <strong>{money(grupo.total)}</strong>
                          {/* Antes iba un "Enviar 0" deshabilitado con el motivo
                              escondido en el title: se cambia por el motivo a la
                              vista. Dice "Aprobar" y no "Enviar" porque eso es lo
                              que hace: autoriza. Lo que no alcance a salir ahora
                              lo suelta el motor cuando pueda. */}
                          {semiautomatico && (porAprobar.length > 0 && !data?.pausado
                            ? <button type="button" disabled={Boolean(busy)} onClick={(event) => { event.stopPropagation(); abrirConfirmacion(grupo); }}>Aprobar {porAprobar.length}</button>
                            : (autorizadas.length === 0 || data?.pausado) && (
                              <span className="av-block-reason">
                                <i className="pi pi-info-circle" />
                                {data?.pausado ? 'Los envíos están pausados.' : (motivosBloqueo(grupo.filas) || 'Nada por enviar en este grupo.')}
                              </span>
                            ))}
                          <i className={`av-group-chevron pi ${abierto ? 'pi-chevron-up' : 'pi-chevron-down'}`} aria-hidden="true" />
                        </header>
                        {abierto && <div className="av-group-rows">{grupo.filas.map(filaAviso)}</div>}
                      </article>
                    );
                  })}
                </section>
              )
            )}
          </div>
        </div>
      </div>
      {confirmacion && resumen && (
        <div className="av-confirm-backdrop" role="presentation" onMouseDown={cerrarConfirmacion}>
          <div className="av-confirm" role="dialog" aria-modal="true" aria-label="Resumen de lo que se va a enviar" onMouseDown={(event) => event.stopPropagation()}>
            <div className="av-modal-head">
              <div>
                <h3>Esto es lo que se va a enviar</h3>
                <p>Aprobar significa <b>autorizo estos mensajes</b>: los que no quepan ahora salen solos, sin volver a pedirte permiso.</p>
              </div>
              <button type="button" aria-label="Cerrar" onClick={cerrarConfirmacion}><i className="pi pi-times" /></button>
            </div>

            {/* 🔴 CUÁNDO SALE. El bloque que faltaba: la tarjeta decía "sale 2
                mensajes", salía uno y el segundo se quedaba esperando sin que
                nadie lo supiera. Aquí se dice antes de apretar. */}
            <div style={{ padding: '12px 18px 0' }}>
              <div className={`av-inline-alert ${(resumen.diferidos > 0 && !saltaEspera) || resumen.mensajes === 0 ? 'warn' : ''}`} style={{ alignItems: 'flex-start' }}>
                <i className={`pi ${resumen.diferidos > 0 && !saltaEspera ? 'pi-clock' : 'pi-send'}`} style={{ marginTop: 2 }} />
                <span style={{ lineHeight: 1.5 }}>
                  {resumen.mensajes === 0 && <b>No queda nada seleccionado para enviar.</b>}
                  {resumen.mensajes > 0 && saltaEspera && (
                    <>
                      <b>
                        {resumen.mensajes === 1
                          ? 'Sale 1 mensaje de WhatsApp, ahora mismo.'
                          : `Salen los ${resumen.mensajes} mensajes de golpe, sin esperar entre uno y otro.`}
                      </b>
                      <br />
                      Elegiste ignorar los {ESPACIADO_MIN} minutos de separación.
                    </>
                  )}
                  {resumen.mensajes > 0 && !saltaEspera && resumen.diferidos === 0 && (
                    <b>
                      {resumen.mensajes === 1
                        ? 'Sale 1 mensaje de WhatsApp, ahora mismo.'
                        : `Salen ${resumen.mensajes} mensajes de WhatsApp, ahora mismo.`}
                    </b>
                  )}
                  {resumen.mensajes > 0 && !saltaEspera && resumen.diferidos > 0 && (
                    <>
                      <b>
                        {resumen.ahora === 0 && (resumen.mensajes === 1
                          ? 'El mensaje no sale ahora: queda autorizado.'
                          : `Ninguno de los ${resumen.mensajes} mensajes sale ahora: todos quedan autorizados.`)}
                        {resumen.ahora === 1 && `De ${resumen.mensajes} mensajes sale 1 ahora; ${resumen.diferidos === 1 ? 'el otro queda autorizado' : `los otros ${resumen.diferidos} quedan autorizados`}.`}
                        {resumen.ahora > 1 && `De ${resumen.mensajes} mensajes salen ${resumen.ahora} ahora; ${resumen.diferidos === 1 ? 'el otro queda autorizado' : `los otros ${resumen.diferidos} quedan autorizados`}.`}
                      </b>
                      <br />
                      {resumen.esperaInicial === 0 && `Al mismo teléfono solo puede salir un mensaje cada ${ESPACIADO_MIN} minutos.`}
                      {resumen.esperaInicial > 0 && resumen.mismoTelefono
                        && `Este teléfono recibió un mensaje hace poco: el siguiente puede salir en ~${minutosTexto(resumen.esperaInicial)}.`}
                      {resumen.esperaInicial > 0 && !resumen.mismoTelefono
                        && `Algunos de estos teléfonos recibieron un mensaje hace menos de ${ESPACIADO_MIN} minutos.`}
                      {' '}El motor los suelta solo conforme se cumple la espera: <b>no hay que volver a aprobar</b>.
                    </>
                  )}
                  {resumen.seJuntan > 0 && (
                    <>
                      <br />
                      {`Son ${resumen.elegidas.length} avisos pero ${resumen.mensajes === 1 ? 'un solo mensaje' : `${resumen.mensajes} mensajes`}: `}
                      la mora de varios autos del mismo teléfono se resume en uno. Por eso los tiempos
                      de aquí abajo son estimados y pueden adelantarse.
                    </>
                  )}
                </span>
              </div>
            </div>

            {/* Lo que sí sale: plantilla y TEXTO. Sin el texto a la vista, el
                cobrador aprueba a ciegas algo que le llega a un cliente. */}
            {resumen.elegidas.length > 0 && (
              <>
                <div className="av-section-title" style={{ margin: '8px 18px 0' }}>
                  Se envía <b>{resumen.elegidas.length}</b>
                </div>
                <div className="av-confirm-list">
                  {resumen.elegidas.map((fila) => {
                    const turno = resumen.turnos[fila.recordatorio_id] || { estado: 'ahora', minutos: 0 };
                    const saleYa = turno.estado === 'ahora' || saltaEspera;
                    return (
                      <div key={fila.recordatorio_id} style={{ alignItems: 'flex-start' }}>
                        <span>
                          <b>{fila.cliente}</b>
                          <small>{fila.clave_contrato} · {fila.auto} · {money(fila.monto)}</small>
                          <small style={{ marginTop: 5, color: '#0E5E80', fontWeight: 800 }}>
                            <i className="pi pi-comment" style={{ marginRight: 5, fontSize: 10 }} />
                            {fila.plantilla_label || fila.plantilla || 'Plantilla sin nombre'}
                          </small>
                          <small style={{
                            display: 'block', marginTop: 5, padding: '8px 10px', borderRadius: 9,
                            background: '#F8FAFC', color: 'var(--ink-2)', fontSize: 11,
                            lineHeight: 1.45, whiteSpace: 'pre-line',
                          }}
                          >
                            {fila.vista_previa || 'Sin vista previa: el texto exacto lo arma el motor al enviarlo.'}
                          </small>
                          <span
                            className={`av-status ${saleYa ? 'green' : 'amber'}`}
                            style={{ ...CHIP_INLINE, marginTop: 6 }}
                          >
                            <i className={`pi ${saleYa ? 'pi-send' : 'pi-clock'}`} />
                            {saleYa && 'Sale ahora'}
                            {!saleYa && turno.estado === 'espera' && `Queda autorizado · sale en ~${minutosTexto(turno.minutos)}`}
                            {!saleYa && turno.estado === 'incierto' && 'Sale ahora o queda autorizado'}
                          </span>
                          {/* Nunca un estado sin explicación: si no sale ya, el
                              porqué va escrito debajo, no en un tooltip. */}
                          {!saleYa && turno.estado === 'espera' && (
                            <small style={{ marginTop: 4 }}>{motivoTexto('espaciado_mismo_telefono')}</small>
                          )}
                          {!saleYa && turno.estado === 'incierto' && (
                            <small style={{ marginTop: 4 }}>
                              El motor decide el turno: al mismo teléfono solo sale un mensaje cada {ESPACIADO_MIN} min, y
                              varios de estos avisos pueden ir juntos en uno. Lo que no salga ahora queda autorizado.
                            </small>
                          )}
                        </span>
                        <button type="button" aria-label={`Quitar a ${fila.cliente}`} title="Quitar de este envío" onClick={() => setSeleccionados((ids) => ids.filter((id) => id !== fila.recordatorio_id))}><i className="pi pi-times" /></button>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Ya autorizados: no se vuelven a aprobar, pero tienen que verse o
                el cobrador cree que se perdieron y los aprueba otra vez. */}
            {resumen.yaAutorizadas.length > 0 && (
              <>
                <div className="av-section-title" style={{ margin: '4px 18px 0' }}>
                  Ya autorizados, en camino <b>{resumen.yaAutorizadas.length}</b>
                </div>
                <div className="av-confirm-list">
                  {resumen.yaAutorizadas.map((fila) => (
                    <div key={fila.recordatorio_id} style={{ alignItems: 'flex-start' }}>
                      <span>
                        <b>{fila.cliente}</b>
                        <small>{fila.clave_contrato} · {fila.auto} · {fila.plantilla_label || fila.plantilla || '—'}</small>
                        {chipAutorizado(fila, { marginTop: 5 })}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Nunca desaparecer sin rastro: lo que no va a salir se escribe con
                su motivo, y lo que quitaste se puede volver a meter. */}
            {(resumen.bloqueadas.length > 0 || resumen.quitadas.length > 0) && (
              <>
                <div className="av-section-title" style={{ margin: '4px 18px 0' }}>
                  No va a salir <b>{resumen.bloqueadas.length + resumen.quitadas.length}</b>
                </div>
                <div className="av-confirm-list">
                  {resumen.quitadas.map((fila) => (
                    <div key={fila.recordatorio_id} style={{ alignItems: 'flex-start' }}>
                      <span>
                        <b>{fila.cliente}</b>
                        <small>{fila.clave_contrato} · {fila.auto}</small>
                        <small style={{ marginTop: 4, color: '#8a6d2a', fontWeight: 700 }}>
                          <i className="pi pi-eye-slash" style={{ marginRight: 5, fontSize: 10 }} />
                          Lo quitaste de este envío.
                        </small>
                      </span>
                      <button
                        type="button"
                        aria-label={`Volver a incluir a ${fila.cliente}`}
                        title="Volver a incluir"
                        style={{ background: '#E5F8EF', color: '#087A4D' }}
                        onClick={() => setSeleccionados((ids) => (ids.includes(fila.recordatorio_id) ? ids : [...ids, fila.recordatorio_id]))}
                      >
                        <i className="pi pi-plus" />
                      </button>
                    </div>
                  ))}
                  {resumen.bloqueadas.map((fila) => (
                    <div key={fila.recordatorio_id} style={{ alignItems: 'flex-start' }}>
                      <span>
                        <b>{fila.cliente}</b>
                        <small>{fila.clave_contrato} · {fila.auto}</small>
                        <small style={{ marginTop: 4, color: '#B91C1C', fontWeight: 700 }}>
                          <i className="pi pi-info-circle" style={{ marginRight: 5, fontSize: 10 }} />
                          {fila.preview_pendiente ? 'Preparando la vista previa…' : motivoTexto(fila.motivo)}
                        </small>
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* 🔴 El switch salta SOLO el espaciado de 30 minutos. Se dice qué
                salta y qué NO: el tope diario, el horario, el opt-out y las
                promesas de pago son la diferencia entre cobrar y hostigar. Por
                eso no dice "forzar" a secas — eso invita a apretarlo sin leer. */}
            {resumen.diferidos > 0 && (
              <div style={{ margin: '10px 18px 0', padding: '11px 12px', border: '1px solid var(--line)', borderRadius: 11, background: '#FAFCFD' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 9, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={forzarEspaciado}
                    onChange={(event) => setForzarEspaciado(event.target.checked)}
                    style={{ marginTop: 3 }}
                  />
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <b style={{ color: 'var(--ink)', fontSize: 12 }}>
                      Ignorar los {ESPACIADO_MIN} minutos entre mensajes y mandarlos todos de una vez
                    </b>
                    <small style={{ color: 'var(--ink-3)', fontSize: 11, lineHeight: 1.45 }}>
                      {resumen.mismoTelefono
                        ? `Este cliente va a recibir ${resumen.mensajes} mensajes seguidos.`
                        : 'Los mensajes que estaban esperando su turno salen de inmediato.'}
                      {' '}Solo se salta esa espera: se siguen respetando el tope de mensajes al día
                      por teléfono, el horario permitido, el «no me escriban» y las promesas de pago.
                    </small>
                  </span>
                </label>
              </div>
            )}

            <div className="av-confirm-actions">
              {/* Nunca un botón muerto: si no hay nada que aprobar, se dice por qué. */}
              {!seleccionados.length && (
                <span className="av-block-reason" style={{ marginRight: 'auto', marginTop: 0 }}>
                  <i className="pi pi-info-circle" />
                  {resumen.filas.length === 0
                    ? 'Este grupo se quedó sin avisos.'
                    : 'Quitaste todos los avisos: no queda nada que aprobar.'}
                </span>
              )}
              <button type="button" onClick={cerrarConfirmacion}>Cancelar</button>
              <button type="button" className="primary" disabled={!seleccionados.length || Boolean(busy)} onClick={confirmar}>
                Aprobar {seleccionados.length}
              </button>
            </div>
          </div>
        </div>
      )}
    </ModalPortal>
  );
};

export default AvisosColaModal;
